import React, { useEffect, useState } from "react";
import {
  CreatePortfolioTemplateQuestion, DeletePortfolioTemplateQuestion,
  GetPortfolioLifestyleTemplate,
  PortfolioLifestyleTemplate,
  QuestionType, ReOrderPortfolioTemplateQuestions, UpdatePortfolioTemplateQuestion
} from "../../../services/PortfolioApi";
import { useParams } from "react-router";
import useAuth from "../../login/AuthContext";
import { MainLayout } from "../../../layout/MainLayout";
import { SliderQuestionsTable } from "../../../components/SliderQuestionsTable";
import { Grid } from "../../../UI/Grid";
import { SelectQuestionsTable } from "../../../components/SelectQuestionsTable";
import { FreeTextQuestionsTable } from "../../../components/FreeTextQuestionsTable";
import sortBy from "ramda/src/sortBy";
import prop from "ramda/src/prop";

const TYPES = {
  FREE_TEXT: 'freeText',
  SLIDER: 'slider',
  SELECT: 'select'
};

interface PortfolioTemplateDetailsProps {

}

export const PortfolioTemplateDetails: React.FC<PortfolioTemplateDetailsProps> = () => {
  const { getAuthToken } = useAuth();
  const { portfolioId, templateId } = useParams<{ portfolioId: string, templateId: string }>();
  const [template, setTemplate] = useState<PortfolioLifestyleTemplate>();

  const loadTemplate = async () => {
    let templateRequest = await GetPortfolioLifestyleTemplate(templateId, portfolioId, await getAuthToken());

    if (templateRequest) {
      setTemplate(templateRequest);
    }
  };

  useEffect(() => {
    loadTemplate();
  }, []);

  if (!template) {
    return (
      <>
        Loading
      </>
    );
  };

  return (
    <MainLayout title={template.name}>
      <PortTemplateQuestions
        template={template}
        portfolioId={portfolioId}
        // @ts-ignore
        setTemplate={setTemplate}
        loadTemplate={loadTemplate}
      />
    </MainLayout>
  );
};

interface PortTemplateQuestionsProps {
  portfolioId: string;
  loadTemplate: () => void;
  setTemplate: () => void;
  template: PortfolioLifestyleTemplate;
}

const PortTemplateQuestions: React.FC<PortTemplateQuestionsProps> = ({ portfolioId, template, setTemplate, loadTemplate }) => {
  const { getAuthToken } = useAuth();

  const onSave = async (questionType: QuestionType, data: any) => {
    const answers = data.answers?.map((x: any) => ({
      answerOrder: x.answerOrder,
      answerText: x.answerText,
      answerId: x.id
    })) || [];

    if (data.id) {
      await UpdatePortfolioTemplateQuestion(data.id, template.id, portfolioId, await getAuthToken(), {
        portfolioId: portfolioId,
        templateId: template.id,
        questionId: data.id,
        questionType: questionType,
        questionText: data.questionText,
        retainAnswer: data.retainAnswer,
        disclaimer: data.disclaimer,
        placeholder: data.placeholder,
        numberOfSliderOptions: data.numberOfSliderOptions,
        answers: answers,
        tagId: data.tag?.id,
      });
    } else {
      await CreatePortfolioTemplateQuestion(template.id, portfolioId, await getAuthToken(), {
        portfolioId: portfolioId,
        templateId: template.id,
        questionType: questionType,
        questionText: data.questionText,
        disclaimer: data.disclaimer,
        placeholder: data.placeholder,
        retainAnswer: data.retainAnswer,
        numberOfSliderOptions: data.numberOfSliderOptions,
        answers: answers,
        tagId: data.tag?.id,
      });
    }

    loadTemplate();
  };

  const onReorderQuestions = async (data: any) => {
    // @ts-ignore
    setTemplate({
      ...template,
      questions: {    // @ts-ignore
        ...template.question,    // @ts-ignore
        [TYPES[data.type]]: data.items
      }
    });

    await ReOrderPortfolioTemplateQuestions(template.id, portfolioId, data.type, data.items.map((question: any) => question.id), await getAuthToken());
  };

  const deleteQuestion = async (questionId: number) => {
    await DeletePortfolioTemplateQuestion(template.id, portfolioId, questionId, await getAuthToken());

    loadTemplate();
  };

  return (
    <Grid item container spacing={5}>
      <Grid item xs={12}>
        <SliderQuestionsTable
          onSave={(data: any) => {
            onSave(QuestionType.SLIDER, data);
            return { success: true };
          }}
          onReorder={(data: any) => { onReorderQuestions(data); }}
          onRemove={(data: any) => {
            deleteQuestion(data.id);
          }}
          questions={sortBy<any>(prop("questionOrder"), template.questions?.slider || [])}
          showTagColumn={true}
          editDisabled={false}
        />
      </Grid>
      <Grid item xs={12}>
        <SelectQuestionsTable
          onSave={(data: any) => {
            onSave(QuestionType.SELECT, data);
            return { success: true };
          }}
          onReorder={(data: any) => { onReorderQuestions(data); }}
          onRemove={(data: any) => {
            deleteQuestion(data.id);
          }}
          questions={sortBy<any>(prop("questionOrder"), template.questions?.select || [])}
          showTagColumn={true}
          editDisabled={false}
        />
      </Grid>
      <Grid item xs={12}>
        <FreeTextQuestionsTable
          onSave={(data: any) => {
            onSave(QuestionType.FREE_TEXT, data);
            return { success: true };
          }}
          onReorder={(data: any) => { onReorderQuestions(data); }}
          onRemove={(data: any) => {
            deleteQuestion(data.id);
          }}
          questions={sortBy<any>(prop("questionOrder"), template.questions?.freeText || [])}
          showTagColumn={true}
          editDisabled={false}
        />
      </Grid>
    </Grid>
  );
};


export const PORTFOLIO_LIFESTYLE_TEMPLATE_DETAILED = "/super_admin/portfolios/:portfolioId/templates/lifestyle/:templateId";